import { formatMontant } from '../utils/formatters';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { rapportsAPI, honorairesAPI } from '../services/api';
import exportsAPI from '../services/exportsAPI';
import ExportModal from '../components/ExportModal';
import Plate from '../components/Plate';
import { PDFGenerationOverlay } from '../components/PDFGenerationOverlay';
import ConfirmModal from '../components/ConfirmModal';
import SmartPagination from '../components/SmartPagination';
import { Plus, Trash2, FileDown, RefreshCw, Eye, Pencil, ReceiptText, Archive, Mail, MailCheck, Send } from 'lucide-react';
import { playSound } from '../utils/SoundPlayer';


import toast from 'react-hot-toast';

const STATUS_MAP = {
    brouillon: { label: 'Brouillon', cls: 'badge-gray' },
    finalise: { label: 'Finalisé', cls: 'badge-blue' },
    pdf_genere: { label: 'PDF Généré', cls: 'badge-green' },
};

// Local Plate component removed in favor of shared component

const RapportsPage = () => {
    const navigate = useNavigate();
    const [rapports, setRapports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statut, setStatut] = useState('');
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [assurance, setAssurance] = useState('');
    const [marque, setMarque] = useState('');
    const [modele, setModele] = useState('');
    const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
    const [exportJobId, setExportJobId] = useState(null);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [pdfStatus, setPdfStatus] = useState('idle');
    const [showEmailConfirm, setShowEmailConfirm] = useState(false);
    const [currentReport, setCurrentReport] = useState(null);

    const fetchRapports = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const res = await rapportsAPI.list({ 
                page, 
                search, 
                statut,
                date_start: dateStart,
                date_end: dateEnd,
                assurance,
                marque,
                modele
            });
            setRapports(res.data.data || []);
            setPagination(res.data.pagination || { total: 0, page: 1, pages: 1 });
        } catch { toast.error('Erreur de chargement'); }
        finally { setLoading(false); }
    }, [search, statut, dateStart, dateEnd, assurance, marque, modele]);

    useEffect(() => { fetchRapports(1); }, [fetchRapports]);

    const handlePDF = async (id, num) => {
        try {
            setPdfStatus('generating');
            const res = await rapportsAPI.generatePDF(id);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Rapport_${num}.pdf`;
            a.click();
            URL.revokeObjectURL(url);

            setPdfStatus('success');
            playSound('pdf');
            setTimeout(() => setPdfStatus('idle'), 1500);
            fetchRapports(pagination.page);

        } catch {
            setPdfStatus('idle');
            toast.error('Erreur lors de la génération du PDF', { id: 'pdf' });
        }
    };

    const handleExport = async (id, num) => {
        try {
            const res = await exportsAPI.createExport(id);
            playSound('exportzip');
            if (res.jobId) {
                setExportJobId(res.jobId);
                setIsExportModalOpen(true);
            } else {

                toast.error("Format de réponse inattendu");
            }
        } catch (error) {
            toast.error('Erreur lors du lancement de l\'exportation');
            console.error(error);
        }
    };

    // Confirm modal state for delete
    const [confirmModal, setConfirmModal] = useState({ open: false, id: null, num: null, loading: false });

    const handleDeleteRequest = (id, num) => {
        setConfirmModal({ open: true, id, num, loading: false });
    };

    const handleDeleteConfirm = async () => {
        setConfirmModal(m => ({ ...m, loading: true }));
        try {
            playSound('delete');
            await rapportsAPI.delete(confirmModal.id);
            toast.success('Rapport supprimé');
            setConfirmModal({ open: false, id: null, num: null, loading: false });
            fetchRapports(pagination.page);
        } catch {
            toast.error('Erreur lors de la suppression');
            setConfirmModal(m => ({ ...m, loading: false }));
        }
    };



    const handleSendEmail = (report) => {
        setCurrentReport(report);
        setShowEmailConfirm(true);
    };

    const executeSendEmail = async () => {
        if (!currentReport) return;
        const { id, numero_rapport } = currentReport;
        setShowEmailConfirm(false);
        const tId = toast.loading(`Envoi du PV ${numero_rapport} par email...`);
        try {
            await rapportsAPI.sendEmail(id);
            toast.success('Email envoyé avec succès au Mandataire', { id: tId });
            playSound('email');
            fetchRapports(pagination.page);
        } catch (err) {

            toast.error(err.response?.data?.message || "Erreur lors de l'envoi de l'email", { id: tId });
        }
    };

    const handleFacturer = async (id) => {
        try {
            const res = await honorairesAPI.list({ rapport_id: id });
            if (res.data.data && res.data.data.length > 0) {
                navigate(`/honoraires/${res.data.data[0].id}`);
            } else {
                navigate(`/honoraires/nouvelle?rapport=${id}`);
            }
        } catch {
            navigate(`/honoraires/nouvelle?rapport=${id}`);
        }
    };

    const formatCurrency = (v) => v ? formatMontant(v) : '—';

    return (
        <Layout title="PV / Rapports d'Expertise">
            <PDFGenerationOverlay status={pdfStatus} />
            <div className="flex-between mb-4">
                <div>
                    <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--primary-800)' }}>PV / Rapports d'Expertise</h2>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '2px' }}>{pagination.total} rapport(s) au total</p>
                </div>
                <button className="btn btn-primary" onClick={() => navigate('/rapports/nouveau')}>
                    <Plus size={16} /> Nouveau PV/Rapport
                </button>
            </div>

            <div className="table-container">
                <div className="table-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '12px', padding: '16px' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <div className="search-box" style={{ flex: 1 }}>
                            <input type="text" placeholder="Rechercher par PV, Sinistre, Assuré..." value={search} onChange={e => setSearch(e.target.value)} />
                            <span className="search-icon">🔍</span>
                        </div>
                        <select className="form-control" style={{ width: 'auto', padding: '8px 12px' }}
                            value={statut} onChange={e => setStatut(e.target.value)}>
                            <option value="">Tous les statuts</option>
                            {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                        <button className="btn btn-ghost btn-sm" onClick={() => fetchRapports(pagination.page)}><RefreshCw size={14} /></button>
                    </div>

                    {/* Advanced Filters Row */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Assurance:</span>
                            <input 
                                type="text" className="form-control" placeholder="Nom..."
                                style={{ width: '130px', height: '36px', fontSize: '12px' }}
                                value={assurance} onChange={e => setAssurance(e.target.value)}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Date Accident:</span>
                            <input 
                                type="date" className="form-control"
                                style={{ width: '130px', height: '36px', fontSize: '12px' }}
                                value={dateStart} onChange={e => setDateStart(e.target.value)}
                            />
                            <span style={{ color: 'var(--text-muted)' }}>au</span>
                            <input 
                                type="date" className="form-control"
                                style={{ width: '130px', height: '36px', fontSize: '12px' }}
                                value={dateEnd} onChange={e => setDateEnd(e.target.value)}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Véhicule:</span>
                            <input 
                                type="text" className="form-control" placeholder="Marque..."
                                style={{ width: '100px', height: '36px', fontSize: '12px' }}
                                value={marque} onChange={e => setMarque(e.target.value)}
                            />
                            <input 
                                type="text" className="form-control" placeholder="Modèle..."
                                style={{ width: '100px', height: '36px', fontSize: '12px' }}
                                value={modele} onChange={e => setModele(e.target.value)}
                            />
                        </div>
                        {(dateStart || dateEnd || assurance || marque || modele) && (
                            <button 
                                className="btn btn-ghost btn-sm" 
                                onClick={() => { setDateStart(''); setDateEnd(''); setAssurance(''); setMarque(''); setModele(''); }}
                                style={{ color: 'var(--danger)', fontSize: '11px', padding: '4px 8px' }}
                            >
                                Reinitialiser
                            </button>
                        )}
                    </div>
                </div>

                {loading ? <div className="loading-spinner"><div className="spinner" /></div> :
                    rapports.length === 0 ? (
                        <div className="empty-state">
                            <p>📄</p>
                            <h3>Aucun rapport trouvé</h3>
                            <button className="btn btn-primary" style={{ marginTop: '12px' }} onClick={() => navigate('/rapports/nouveau')}>
                                <Plus size={14} /> Nouveau PV/Rapport
                            </button>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table>
                                <thead>
                                    <tr>
                                        <th style={{ whiteSpace: 'nowrap' }}>P.V</th>
                                        <th style={{ textAlign: 'center', width: '60px' }}>Assur</th>
                                        <th style={{ whiteSpace: 'nowrap' }}>RÉF. SINISTRÉ</th>
                                        <th>Assuré</th>
                                        <th>Véhicule</th>
                                        <th style={{ whiteSpace: 'nowrap' }}>Date</th>
                                        <th style={{ whiteSpace: 'nowrap' }}>Total</th>
                                        <th style={{ textAlign: 'center', width: '30px' }}>📷</th>
                                        <th>Statut</th>
                                        <th style={{ width: '220px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(rapports) && rapports.map(r => (
                                        <tr key={r.id}>
                                            <td style={{ fontWeight: '700', color: 'var(--primary-600)', fontFamily: 'monospace', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                                {r.numero_rapport || '—'}
                                                {r.email_sent ? (
                                                    <MailCheck size={14} style={{ color: 'var(--success)', marginLeft: '6px', verticalAlign: 'middle' }} title={`Envoyé le ${new Date(r.email_sent_at).toLocaleString()}`} />
                                                ) : null}
                                            </td>
                                            <td style={{ textAlign: 'center', padding: '4px' }}>
                                                {r.assurance_logo ? (
                                                    <img
                                                        src={`/assurances_logo/${r.assurance_logo}`}
                                                        alt={r.nom_assurance}
                                                        title={r.nom_assurance}
                                                        style={{ height: '35px', maxWidth: '100px', objectFit: 'contain' }}
                                                    />
                                                ) : (
                                                    <span style={{ fontSize: '9px', color: 'var(--text-muted)' }} title={r.nom_assurance}>{r.nom_assurance || '—'}</span>
                                                )}
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: '800', fontSize: '12px', color: 'var(--primary-700)', letterSpacing: '0.3px' }}>
                                                    {r.reference_interne || '—'}
                                                </div>
                                                {r.numero_sinistre && (
                                                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'monospace', marginTop: '2px' }}>
                                                        N° Sin.Assur: {r.numero_sinistre}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ fontWeight: '600', fontSize: '12px' }}>{r.nom_assure || '—'}</td>
                                            <td>
                                                <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{(r.marque_vehicule || '') + ' ' + (r.modele_vehicule || '')}</div>
                                                <div style={{ marginTop: '2px' }}><Plate value={r.immatriculation} /></div>
                                            </td>
                                            <td style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>{r.date_rapport ? new Date(r.date_rapport).toLocaleDateString('fr-DZ') : '—'}</td>
                                            <td>
                                                <span style={{ fontWeight: '700', color: 'var(--primary-700)', fontSize: '12px', whiteSpace: 'nowrap' }}>{formatCurrency(r.total_general)}</span>
                                            </td>
                                            <td style={{ textAlign: 'center', fontSize: '11px' }}>{r.nb_photos || 0}</td>
                                            <td>
                                                <span className={`badge ${STATUS_MAP[r.statut]?.cls || 'badge-gray'}`} style={{ fontSize: '10px', padding: '2px 6px' }}>{STATUS_MAP[r.statut]?.label || r.statut || '—'}</span>
                                            </td>
                                            <td>
                                                <div className="table-actions">
                                                    <button className="btn btn-ghost btn-sm action-view" title="Voir" onClick={() => navigate(`/rapports/${r.id}`)}>
                                                        <Eye size={18} />
                                                    </button>
                                                    <button className="btn btn-ghost btn-sm action-edit" title="Modifier" onClick={() => navigate(`/rapports/${r.id}/modifier`)}>
                                                        <Pencil size={18} />
                                                    </button>
                                                    <button className="btn btn-ghost btn-sm action-invoice" title="Facturer" onClick={() => handleFacturer(r.id)}>
                                                        <ReceiptText size={18} />
                                                    </button>
                                                    <button className="btn btn-ghost btn-sm action-pdf" title="PDF" onClick={() => handlePDF(r.id, r.numero_rapport)}>
                                                        <FileDown size={18} />
                                                    </button>
                                                    <button className="btn btn-ghost btn-sm action-export" title="ZIP" onClick={() => handleExport(r.id, r.numero_rapport)}>
                                                        <Archive size={18} />
                                                    </button>
                        <button className="btn btn-ghost btn-sm action-delete" title="Supprimer" onClick={() => handleDeleteRequest(r.id, r.numero_rapport)}>
                                                        <Trash2 size={18} />
                                                    </button>

                                                    {r.statut === 'finalise' && (
                                                        <button 
                                                            className="btn btn-ghost btn-sm action-send-email" 
                                                            title="Envoyer au Mandataire" 
                                                            style={{ color: r.email_sent ? 'var(--success)' : 'var(--primary-600)' }}
                                                            onClick={() => handleSendEmail(r)}
                                                        >
                                                            {r.email_sent ? <MailCheck size={18} /> : <Send size={18} />}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                {/* Smart Pagination */}
                <SmartPagination
                    page={pagination.page}
                    pages={pagination.pages}
                    total={pagination.total}
                    limit={pagination.limit || 20}
                    onPageChange={(p) => fetchRapports(p)}
                />
            </div>

            {/* Confirm Delete Modal */}
            <ConfirmModal
                open={confirmModal.open}
                title="Supprimer le Rapport"
                message={`Voulez-vous vraiment supprimer le rapport ${confirmModal.num} ? Cette action est irréversible.`}
                confirmLabel="Oui, supprimer"
                cancelLabel="Annuler"
                variant="danger"
                loading={confirmModal.loading}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setConfirmModal({ open: false, id: null, num: null, loading: false })}
            />
            <ExportModal
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                jobId={exportJobId}
            />
            <ConfirmModal
                open={showEmailConfirm}
                onCancel={() => setShowEmailConfirm(false)}
                onConfirm={executeSendEmail}
                title="Confirmation d'envoi d'email"
                confirmLabel="Oui, envoyer"
                cancelLabel="Annuler"
                variant="info"
            >

                <div style={{ textAlign: 'center' }}>
                    <p style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
                        Voulez-vous envoyer ce rapport par email au mandataire ?
                    </p>
                    <div style={{ background: 'var(--surface-2)', padding: '15px', borderRadius: '12px', borderLeft: '4px solid var(--primary-500)', textAlign: 'left' }}>
                         <div style={{ marginBottom: '8px' }}>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 800 }}>Compagnie:</span>
                            <div style={{ fontWeight: 700, color: 'var(--primary-800)' }}>{currentReport?.nom_assurance || 'Non spécifiée'}</div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                             <div>
                                <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 800 }}>Code:</span>
                                <div style={{ fontWeight: 700 }}>{currentReport?.code_assurance || '—'}</div>
                            </div>
                            <div>
                                <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 800 }}>Ville:</span>
                                <div style={{ fontWeight: 700 }}>{currentReport?.ville_assurance || '—'}</div>
                            </div>
                        </div>
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed var(--border)' }}>
                             <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 800 }}>Email Destinataire:</span>
                             <div style={{ fontWeight: 700, color: 'var(--primary-600)' }}>{currentReport?.assurance_email || 'Email non configuré'}</div>
                        </div>
                    </div>
                </div>
            </ConfirmModal>
        </Layout>
    );
};

export default RapportsPage;
